/**
 * Tela de trabalho. Abas: Minhas · Lista · Quadro · Matriz · Calendário.
 * Filtros e a tarefa aberta no painel lateral vivem na URL (o painel e o
 * calendário apontam para cá com filtros prontos).
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useInvalidarTarefas } from '../lib/tarefas';
import { Sec, Tabs } from '../ui/layout';
import { useToast } from '../ui/toast';
import { ModalAvulsa } from '../telas/agenda/avulsa';
import { GestaoTarefas } from '../telas/agenda/gestao';
import { CalendarioTarefas } from '../telas/agenda/calendario';
import { DrawerTarefa } from '../telas/agenda/drawer';
import { BarraFiltros, type Filtros } from '../telas/agenda/filtros';
import { ListaTarefas } from '../telas/agenda/lista';
import { MatrizTarefas } from '../telas/agenda/matriz';
import { FechamentoMes } from '../telas/agenda/fechamento';
import { QuadroTarefas } from '../telas/agenda/quadro';
import { AbaRecorrentes } from '../telas/agenda/recorrentes';
import { ModalParcelamento } from '../telas/clientes/parcelamentos';
import { useIntegraAtivo } from '../telas/integra/cliente';
import { ModalDasLote } from '../telas/integra/das-lote';

const Busca = z.object({
  aba: z.enum(['minhas', 'lista', 'quadro', 'matriz', 'fechamento', 'calendario', 'recorrentes', 'gestao']).optional(),
  situacao: z.enum(['ATIVA', 'FORA_DO_PLANO', 'TODAS']).optional(),
  t: z.string().optional(),
  de: z.string().optional(),
  ate: z.string().optional(),
  dep: z.string().optional(),
  resp: z.string().optional(),
  status: z.enum(['PENDENTE', 'EM_ANDAMENTO', 'AGUARDANDO_TERCEIRO', 'EM_REVISAO', 'CONCLUIDA', 'DISPENSADA', 'CANCELADA']).optional(),
  busca: z.string().optional(),
  atrasadas: z.boolean().optional(),
  semResp: z.boolean().optional(),
  interno: z.boolean().optional(),
  /** painel (fatia 2a): guias com pagamento avisado pelo cliente (7 dias) / com pedido de guia atualizada — qualquer status */
  pagAviso: z.boolean().optional(),
  atualizada: z.boolean().optional(),
});

export const Route = createFileRoute('/_app/tarefas')({ validateSearch: Busca, component: Tarefas });

function Tarefas() {
  const s = Route.useSearch();
  const nav = useNavigate({ from: Route.fullPath });
  const toast = useToast();
  const { eu } = useAuth();
  const papel = eu?.escritorio?.papel;
  const podeEditar = papel === 'ADMIN' || papel === 'SUPERVISOR' || papel === 'OPERADOR';
  const supervisor = papel === 'ADMIN' || papel === 'SUPERVISOR';
  const invalidar = useInvalidarTarefas();
  const [novaAberta, setNovaAberta] = useState(false);
  const [novoParcelamento, setNovoParcelamento] = useState(false);
  const [dasLote, setDasLote] = useState(false);
  const integra = useIntegraAtivo();
  const qc = useQueryClient();
  const aba = s.aba ?? 'minhas';
  const f: Filtros = { de: s.de, ate: s.ate, dep: s.dep, resp: s.resp, status: s.status, busca: s.busca, atrasadas: s.atrasadas, semResp: s.semResp, interno: s.interno, pagAviso: s.pagAviso, atualizada: s.atualizada };
  const setBusca = (p: Partial<typeof s>) => void nav({ search: (prev) => ({ ...prev, ...p }), replace: true });
  const abrir = (id: string) => setBusca({ t: id });
  const fechar = () => setBusca({ t: undefined });

  const reconciliar = useMutation({
    mutationFn: () => post<{ criadas: number; atualizadas: number; removidas: number; canceladas: number; divergentes: number; avisos: string[] }>('/v1/agenda/reconciliar', {}),
    onSuccess: (r) => { invalidar(); toast.ok(`prazos atualizados: ${r.criadas} tarefa(s) nova(s), ${r.atualizadas} com prazo recalculado, ${r.removidas} retirada(s) por não serem mais previstas${r.canceladas ? `, ${r.canceladas} mantida(s) para você decidir (tinham registros)` : ''}${r.divergentes ? `, ${r.divergentes} com prazo divergente` : ''}`); if (r.avisos.length) toast.erro(r.avisos[0]!); },
    onError: (e) => toast.erro(e),
  });

  return (
    <>
      <div className="topbar">
        <div><h1>Tarefas</h1><div className="sub">{aba === 'gestao' ? 'filtre, ajuste, refaça o período e exclua as tarefas geradas — em qualquer situação' : aba === 'minhas' ? 'o que está com você, por prazo' : aba === 'fechamento' ? 'por cliente, o que falta em cada obrigação do mês — concluir, registrar a entrega, publicar ao cliente' : aba === 'matriz' ? 'cliente × obrigação — clique na célula' : aba === 'quadro' ? 'arraste os cartões entre as colunas' : aba === 'calendario' ? 'vencimentos por dia' : aba === 'recorrentes' ? 'tarefas que se repetem e parcelamentos, de todos os clientes' : 'todas as tarefas do escritório'}</div></div>
        <div className="acoes">
          {podeEditar && integra.ativo && <button className="btn" type="button" title="emite o DAS de todos os clientes do Simples de uma competência pelo Integra Contador (uma chamada cobrada por cliente)" onClick={() => setDasLote(true)}>⚡ DAS em lote…</button>}
          {podeEditar && <Link className="btn" to="/guias" title="solte os PDFs das guias: o sistema acha a tarefa de cada uma e registra">📄 Caixa de entrada de guias</Link>}
          {supervisor && <button className="btn" type="button" disabled={reconciliar.isPending} onClick={() => reconciliar.mutate()}>{reconciliar.isPending ? 'atualizando…' : 'Atualizar prazos'}</button>}
          {podeEditar && <button className="btn" type="button" title="parcelamento de tributos: uma tarefa por parcela" onClick={() => setNovoParcelamento(true)}>+ Parcelamento</button>}
          {podeEditar && <button className="btn primario" type="button" title="avulsa (uma vez) ou recorrente (se repete) — escolha no cadastro de tarefas ou escreva outra" onClick={() => setNovaAberta(true)}>+ Tarefa</button>}
        </div>
      </div>
      <div className="conteudo">
        <Tabs valor={aba} onChange={(a) => setBusca({ aba: a })} itens={[
          { id: 'minhas', rotulo: 'Minhas' }, { id: 'lista', rotulo: 'Lista' }, { id: 'quadro', rotulo: 'Quadro' }, { id: 'matriz', rotulo: 'Matriz' }, { id: 'fechamento', rotulo: 'Fechamento' }, { id: 'calendario', rotulo: 'Calendário' }, { id: 'recorrentes', rotulo: 'Recorrentes' }, ...(supervisor ? [{ id: 'gestao' as const, rotulo: 'Gestão' }] : []),
        ]} />
        {aba !== 'recorrentes' && aba !== 'gestao' && <Sec titulo="Filtros">
          <BarraFiltros f={f} compacto={aba === 'minhas' || aba === 'calendario'} onChange={(p) => setBusca(p)} />
        </Sec>}
        {aba === 'minhas' && <ListaTarefas f={f} minhas onAbrir={abrir} />}
        {aba === 'lista' && <ListaTarefas f={f} onAbrir={abrir} />}
        {aba === 'quadro' && <QuadroTarefas f={f} onAbrir={abrir} />}
        {aba === 'matriz' && <MatrizTarefas f={f} onAbrir={abrir} />}
        {aba === 'fechamento' && <FechamentoMes f={f} onAbrir={abrir} />}
        {aba === 'recorrentes' && <AbaRecorrentes onAbrirTarefa={abrir} />}
        {aba === 'gestao' && <GestaoTarefas onAbrir={abrir} inicial={{ situacao: s.situacao, busca: s.busca }} />}
        {aba === 'calendario' && <CalendarioTarefas f={f} onDia={(d) => setBusca({ aba: 'lista', de: d, ate: d, status: undefined, atrasadas: undefined })} />}
      </div>
      {s.t && <DrawerTarefa id={s.t} onFechar={fechar} onAbrir={abrir} />}
      <ModalAvulsa aberto={novaAberta} onFechar={() => setNovaAberta(false)} onCriada={(id) => { setNovaAberta(false); abrir(id); }} />
      {novoParcelamento && <ModalParcelamento onFechar={() => setNovoParcelamento(false)} onCriado={() => { void qc.invalidateQueries({ queryKey: ['parcelamentos'] }); invalidar(); }} />}
      {dasLote && <ModalDasLote onFechar={() => setDasLote(false)} />}
    </>
  );
}
